/**
 * JSON-LD helpers for SEO schema markup.
 * Schema reference: https://schema.org/
 */
import { SITE_URL } from '@/lib/api';

const ORG_NAME = 'Ads Dashbound';
const ORG_LEGAL_NAME = 'DASHBOUND MEDIA - FZCO';
const ORG_EMAIL = 'contact@dashbound.media';
const ORG_LOGO = `${SITE_URL}/og-image.png`;
const ORG_DESCRIPTION =
  'Ads Dashbound is a performance traffic platform for advertisers, agencies, and media buyers using curated traffic formats, source-level controls, AI-assisted routing signals, fraud filtering, and top-up based campaign launch support.';
const SITE_DESCRIPTION =
  'Premium performance traffic platform for advertisers — curated traffic formats, source-level controls, AI-assisted routing signals, fraud filtering, and top-up funded campaigns.';

/**
 * Returns Organization JSON-LD with legalName, email, address, description and
 * contactPoint. `sameAs` is intentionally omitted unless real verified social
 * profiles are provided via the optional argument.
 */
export function organizationJsonLd({ sameAs } = {}) {
  const node = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: ORG_NAME,
    legalName: ORG_LEGAL_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: ORG_LOGO,
      width: 1200,
      height: 630,
    },
    image: ORG_LOGO,
    email: ORG_EMAIL,
    description: ORG_DESCRIPTION,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Building A1, Dubai Digital Park',
      addressLocality: 'Dubai Silicon Oasis',
      addressRegion: 'Dubai',
      addressCountry: 'AE',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: ORG_EMAIL,
        contactType: 'customer support',
        areaServed: 'Worldwide',
        availableLanguage: ['English'],
      },
    ],
  };
  if (Array.isArray(sameAs) && sameAs.length > 0) {
    node.sameAs = sameAs;
  }
  return node;
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: ORG_NAME,
    alternateName: 'Dashbound.media',
    url: SITE_URL,
    description: SITE_DESCRIPTION,
    inLanguage: 'en',
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
  };
}

/**
 * Build a BreadcrumbList JSON-LD object.
 * @param {{label: string, path?: string}[]} items
 *   Last item should typically be the current page (no `path`).
 */
export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => {
      const entry = {
        '@type': 'ListItem',
        position: idx + 1,
        name: item.label,
      };
      if (item.path) entry.item = `${SITE_URL}${item.path}`;
      return entry;
    }),
  };
}

/**
 * Build a Service JSON-LD object.
 */
export function serviceJsonLd({
  name,
  description,
  serviceType,
  url,
  areaServed = 'Worldwide',
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    serviceType,
    areaServed,
    url: url ? `${SITE_URL}${url}` : SITE_URL,
    provider: {
      '@type': 'Organization',
      name: ORG_NAME,
      url: SITE_URL,
    },
  };
}

/**
 * Returns LocalBusiness JSON-LD focused on the Dubai HQ for local SEO indexing.
 * Used on the Contact page.
 */
export function localBusinessJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#localbusiness`,
    name: ORG_NAME,
    legalName: ORG_LEGAL_NAME,
    url: SITE_URL,
    image: ORG_LOGO,
    logo: ORG_LOGO,
    email: ORG_EMAIL,
    description: ORG_DESCRIPTION,
    priceRange: 'Top-up funded campaigns',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Building A1, Dubai Digital Park',
      addressLocality: 'Dubai Silicon Oasis',
      addressRegion: 'Dubai',
      addressCountry: 'AE',
    },
    areaServed: 'Worldwide',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: ORG_EMAIL,
        contactType: 'customer support',
        areaServed: 'Worldwide',
        availableLanguage: ['English'],
      },
    ],
    parentOrganization: {
      '@id': `${SITE_URL}/#organization`,
    },
  };
}

/**
 * Combine multiple JSON-LD objects into a @graph wrapper.
 */
export function graph(...nodes) {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes.map((n) => {
      // Strip outer @context — already on the wrapper
      const copy = { ...n };
      delete copy['@context'];
      return copy;
    }),
  };
}
